/**
 * Centralized API Endpoints
 * جميع مسارات الـ APIs في ملف واحد
 * يتم الحصول على التوكن والـ tenant بعد عملية الـ login
 * @module api/endpoints
 */

import { AUTH_CONFIG } from '@/config/constants';

const TENANT_ID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const ORG_ID_RE = /^[A-Z]{2}-\d{4}$/;

export const isValidTenantId = (tenantId) =>
  typeof tenantId === 'string' && TENANT_ID_RE.test(tenantId.trim());

export const isValidOrgId = (orgId) =>
  typeof orgId === 'string' && ORG_ID_RE.test(orgId);

/**
 * Get current tenant ID from localStorage or constants
 * يتم الحصول على الـ tenant ID من localStorage بعد عملية الـ login
 * @returns {string} - Tenant ID
 */
export const getTenantId = () => {
  const stored = localStorage.getItem('tenantId');
  if (isValidTenantId(stored)) return stored;
  return process.env.REACT_APP_TENANT_ID;
};

/**
 * Get organization ID (orgId) in XX-0000 format from localStorage
 * يتم الحصول على معرف المؤسسة بتنسيق XX-0000 من localStorage
 * @returns {string|null} - Organization ID or null
 */
export const getOrgId = () => {
  const orgId = localStorage.getItem('orgId');
  return isValidOrgId(orgId) ? orgId : null;
};

/**
 * Store organization ID (orgId) in XX-0000 format to localStorage
 * حفظ معرف المؤسسة بتنسيق XX-0000 في localStorage
 * @param {string} orgId - Organization ID in XX-0000 format
 */
export const setOrgId = (orgId) => {
  if (isValidOrgId(orgId)) {
    localStorage.setItem('orgId', orgId);
  }
};

/**
 * Get company name from localStorage
 * الحصول على اسم الشركة من localStorage
 * @returns {string|null} - Company name or null
 */
export const getCompanyName = () => {
  return localStorage.getItem('companyName');
};

/**
 * Store company name to localStorage
 * حفظ اسم الشركة في localStorage
 * @param {string} companyName - Company name
 */
export const setCompanyName = (companyName) => {
  if (companyName) {
    localStorage.setItem('companyName', companyName);
  }
};

/**
 * Get current token from localStorage
 * يتم الحصول على التوكن من localStorage بعد عملية الـ login
 * @returns {string|null} - Current token or null
 */
export const getToken = () => {
  return localStorage.getItem(AUTH_CONFIG.TOKEN_STORAGE_KEY);
};

/**
 * API Endpoints Configuration
 * جميع الـ endpoints منظمة حسب المجال
 */
export const ENDPOINTS = {
  // ============================================
  // Authentication Endpoints
  // ============================================
  AUTH: {
    LOGIN: '/auth/login',
    SUPER_ADMIN_LOGIN: '/auth/super-admin/login',
    REGISTER: '/auth/register',
    LOGOUT: '/auth/logout',
    VERIFY: '/auth/verify',
    REFRESH: '/auth/refresh',
    ME: '/auth/me',
    CHANGE_PASSWORD: '/auth/change-password',
    FORGET_PASSWORD: '/auth/forget-password',
    RESET_PASSWORD: '/auth/reset-password',
    DELETE_ACCOUNT: '/auth/delete-account',
  },

  TRACKING: {
    SCREENSHOT: '/tracking/screenshot',
  },

  // ============================================
  // Employee Endpoints
  // ============================================
  EMPLOYEES: {
    BASE: '/employees',
    ME: '/employees/me',
    BY_ID: (id) => `/employees/${id}`,
    CREATE: '/employees',
    UPDATE: (id) => `/employees/${id}`,
    DELETE: (id) => `/employees/${id}`,
    APPROVE: (id) => `/employees/${id}/approve`,
    REJECT: (id) => `/employees/${id}/reject`,
    UPDATE_ROLE: (id) => `/employees/${id}/role`, // PATCH endpoint for updating employee role
    
    // Employee Requests
    REQUESTS: '/employees/requests',
    REQUESTS_BY_EMPLOYEE: (employeeId) => `/employees/requests/${employeeId}`,
    APPROVE_REQUEST: (employeeId, requestId) => `/employees/requests/${employeeId}/${requestId}/approve`,
    REJECT_REQUEST: (employeeId, requestId) => `/employees/requests/${employeeId}/${requestId}/reject`,
    
    // Employee Notifications
    NOTIFICATIONS: (employeeId) => `/employees/notifications/${employeeId}`,
    MARK_NOTIFICATION_READ: (employeeId, notificationId) => `/employees/notifications/${employeeId}/${notificationId}`,
    MARK_ALL_NOTIFICATIONS_READ: (employeeId) => `/employees/notifications/mark-all-read/${employeeId}`,
    
    // Employee Documents
    DOCUMENTS: '/employees/documents',
    DOCUMENTS_BY_ID: (id) => `/employees/documents/${id}`,
    DELETE_DOCUMENT: '/employees/documents/delete',
    
    // Device Requests
    DEVICE_REQUESTS: '/employees/device-requests',
    DEVICE_REQUEST_APPROVE: (id) => `/employees/device-requests/${id}/approve`,
    DEVICE_REQUEST_REJECT: (id) => `/employees/device-requests/${id}/reject`,
    DELETE_DEVICE: (deviceId) => `/employees/user/device/${deviceId}`,
    
    // Role Management
    PROMOTE_TO_ADMIN: (id) => `/employees/promote-to-admin/${id}`,
    DEMOTE_TO_EMPLOYEE: (id) => `/employees/demote-to-employee/${id}`,
    
    // Positions
    POSITIONS: (page = 1, limit = 100) => `/employees/positions?page=${page}&limit=${limit}`,
    
    // Allowances
    ALLOWANCES: (id) => `/employees/${id}/allowances`, // POST endpoint for adding allowance
    UPDATE_ALLOWANCE: (id, allowanceId) => `/employees/${id}/allowances/${allowanceId}`, // PATCH endpoint for updating allowance
    DELETE_ALLOWANCE: (id, allowanceId) => `/employees/${id}/allowances/${allowanceId}`, // DELETE endpoint for deleting allowance
    RENEW_CONTRACT: (id) => `/employees/${id}/renew-contract`,
    RENEW_RESIDENCE: (id) => `/employees/${id}/renew-residence`,
  },

  // ============================================
  // Payroll Endpoints
  // ============================================
  PAYROLL: {
    BASE: '/payroll',
    GENERATE: '/payroll/generate',
    STATS: '/payroll/stats',
    STATS_SUMMARY: '/payroll/stats/summary',
    BY_EMPLOYEE: (employeeId) => `/payroll/${employeeId}`,
    PAY_EMPLOYEE: (employeeId) => `/payroll/${employeeId}/pay`,
    PAY_ALL: '/payroll/pay-all',
    PARTIAL_PAY: (employeeId) => `/payroll/${employeeId}/partial-pay`,
    REMOVE_PARTIAL_PAYMENT: (employeeId, paymentId) => `/payroll/${employeeId}/partial-payments/${paymentId}`,
    ADJUSTMENTS: (employeeId) => `/payroll/${employeeId}/adjustments`,
    REMOVE_ADJUSTMENT: (employeeId, adjustmentId) => `/payroll/${employeeId}/adjustments/${adjustmentId}`,
    CALCULATE_CURRENT_MONTH: '/payroll/calculate-current-month',
    CURRENT_MONTH: '/payroll/current-month',
    MY_PAYSLIP: '/payroll/my-payslip',
    MY_PAYROLL: '/payroll/my-payroll',
    MY_MONTHS: '/payroll/my-months',
    PAYSLIP: (employeeId, month) => `/payroll/${employeeId}/payslip?month=${month}`,
  },

  // ============================================
  // Client Endpoints
  // ============================================
  CLIENTS: {
    BASE: '/clients',
    BY_ID: (id) => `/clients/${id}`,
    CREATE: '/clients',
    UPDATE: (id) => `/clients/${id}`,
    DELETE: (id) => `/clients/${id}`,
    FILES: (id) => `/clients/${id}/files`,
    UPLOAD_FILE: (id) => `/clients/${id}/files`,
    DELETE_FILE: (id, fileId) => `/clients/${id}/files/${fileId}`,
    MESSAGES: (id) => `/clients/${id}/messages`,
    SEND_MESSAGE: (id) => `/clients/${id}/messages`,
  },

  // ============================================
  // User Profile Endpoints
  // ============================================
  USERS: {
    PROFILE: '/users/me',
    UPDATE_PROFILE: '/users/me',
    UPDATE_PASSWORD: '/users/me/password',
    AVATAR: '/users/me/avatar',
    DOCUMENTS: '/users/me/documents',
    UPLOAD_DOCUMENT: '/users/me/documents',
    DELETE_DOCUMENT: (documentId) => `/users/me/documents/${documentId}`,
    REQUESTS: '/users/me/requests',
    CREATE_REQUEST: '/users/me/requests',
    NOTIFICATIONS: '/users/me/notifications',
    MARK_NOTIFICATION_READ: (notificationId) => `/users/me/notifications/${notificationId}/read`,
  },

  // ============================================
  // Attendance Endpoints
  // ============================================
  ATTENDANCE: {
    CHECK_IN: '/attendance/check-in',
    CHECK_OUT: '/attendance/check-out',
    STATUS: '/attendance/status',
    TODAY: '/attendance/today',
    STATS: '/attendance/stats',
    EMPLOYEE_TODAY: '/attendance/employee/me/today',
    EMPLOYEE_RECORDS: '/attendance/employee/me',
    TEAM_STATS: '/attendance/team-stats',
    TEAM_DAILY: '/attendance/team-daily',
  },

  // ============================================
  // Daily Attendance Endpoints
  // ============================================
  DAILY_ATTENDANCE: {
    BASE: '/daily-attendance',
    MONTHLY: (employeeId, year, month) => `/daily-attendance/monthly/${employeeId}/${year}/${month}`,
    UPDATE: (recordId) => `/daily-attendance/update/${recordId}`,
    BULK_UPDATE: '/daily-attendance/bulk-update',
    REGENERATE: (employeeId, year, month) => `/daily-attendance/regenerate/${employeeId}/${year}/${month}`,
    RESET_FROM_TODAY: (employeeId) => `/daily-attendance/reset-from-today/${employeeId}`,
    SYNC_MONTH: (employeeId) => `/daily-attendance/sync-month/${employeeId}`,
    USER_RECORDS: (userId) => `/daily-attendance/user-records/${userId}`,
    DAILY_STATS: (employeeId) => `/attendance/${employeeId}/daily-attendance-stats`,
    HOLIDAYS: '/daily-attendance/holidays',
    AUTO_UPDATE_DAILY: '/daily-attendance/auto-update-daily',
    REGENERATE_MONTHLY: '/daily-attendance/regenerate-monthly',
  },

  // ============================================
  // Permission Requests Endpoints (أذونات — separate from leave-requests)
  // ============================================
  PERMISSION_REQUESTS: {
    CREATE: '/permission-requests',
    LIST: (page = 1, limit = 25, status = '') => {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', limit);
      if (status) params.append('status', status);
      return `/permission-requests?${params.toString()}`;
    },
    MY_REQUESTS: (status = '') => {
      const params = new URLSearchParams();
      if (status) params.append('status', status);
      return `/permission-requests/my-requests${params.toString() ? `?${params.toString()}` : ''}`;
    },
    BY_ID: (id) => `/permission-requests/${id}`,
    CANCEL: (id) => `/permission-requests/${id}/cancel`,
    APPROVE: (id) => `/permission-requests/${id}/approve`,
    REJECT: (id) => `/permission-requests/${id}/reject`,
    FORWARD: (id) => `/permission-requests/${id}/forward`,
    TAKE_OVER: (id) => `/permission-requests/${id}/take-over`,
    SETTINGS: '/permission-requests/settings',
    EMPLOYEE_SETTINGS: (employeeId) => `/permission-requests/settings/employee/${employeeId}`,
    PENDING_COUNT: '/permission-requests/pending-count',
  },

  // ============================================
  // Leave Requests Endpoints
  // ============================================
  LEAVE_REQUESTS: {
    DASHBOARD: '/leave-requests/dashboard',
    BALANCE: '/leave-requests/balance',
    CREATE: '/leave-requests',
    LIST: (page = 1, limit = 25) => {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', limit);
      return `/leave-requests?${params.toString()}`;
    },
    BY_ID: (id) => `/leave-requests/${id}`,
    CANCEL: (id) => `/leave-requests/${id}/cancel`,
    APPROVE: (id) => `/leave-requests/${id}/approve`,
    REJECT: (id) => `/leave-requests/${id}/reject`,
    HISTORY: (page = 1, limit = 25, status = '') => {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', limit);
      if (status) params.append('status', status);
      return `/leave-requests/history?${params.toString()}`;
    },
    MY_REQUESTS: (status = '') => {
      const params = new URLSearchParams();
      if (status) params.append('status', status);
      return `/leave-requests/my-requests${params.toString() ? `?${params.toString()}` : ''}`;
    },
  },

  // ============================================
  // Department Endpoints
  // ============================================
  DEPARTMENTS: {
    BASE: '/departments',
    CREATE: '/departments',
    BY_ORG: (orgId) => `/departments/by-org/${orgId}`,
    UPDATE: (id) => `/departments/${id}`,
    DELETE: (id) => `/departments/${id}`,
  },

  // ============================================
  // Branches Endpoints
  // ============================================
  BRANCHES: {
    BASE: '/Branchs', // GET endpoint - capital B as per API
    CREATE: '/Branchs', // POST endpoint - capital B as per API
    UPDATE: (id) => `/Branchs/${id}`, // PUT/PATCH endpoint - capital B as per API
    PATCH: (id) => `/Branchs/${id}`, // PATCH endpoint - capital B as per API
    DELETE: (id) => `/Branchs/${id}`, // DELETE endpoint - capital B as per API
    BY_ID: (id) => `/Branchs/${id}`, // GET by ID endpoint - capital B as per API
    SETTINGS: (id) => `/branches/${id}/settings`, // PATCH endpoint for branch settings (timezone, workHours)
    GET_SETTINGS: (id) => `/branches/${id}/settings`, // GET endpoint for branch settings
    LATE_PENALTY_SETTINGS: (id) => `/branches/${id}/late-penalty-settings`, // GET/PATCH late penalty settings
    AUTO_CHECKOUT_SETTINGS: (id) => `/branches/${id}/auto-checkout-settings`, // GET/PATCH auto-checkout settings
    AUTO_CHECKOUT_DEDUCTION_SETTINGS: (id) => `/branches/${id}/auto-checkout-deduction-settings`, // GET/PATCH auto-checkout deduction (branch or inherit org)
    CHECK_IN_SELFIE_SETTINGS: (id) => `/branches/${id}/check-in-selfie-settings`, // GET/PATCH check-in selfie (branch or inherit org)
  },

  // ============================================
  // Organizations Endpoints
  // ============================================
  ORGANIZATIONS: {
    SETTINGS: (orgId) => `/organizations/api/${orgId}/settings`, // PATCH endpoint for organization settings
    GET_SETTINGS: (orgId) => `/organizations/${orgId}/settings`, // GET endpoint for organization settings
    LATE_PENALTY_SETTINGS: '/organizations/me/late-penalty-settings', // GET/PATCH endpoint for organization late penalty settings
    AUTO_CHECKOUT_SETTINGS: '/organizations/me/auto-checkout-settings', // GET/PATCH organization auto-checkout settings
    AUTO_CHECKOUT_DEDUCTION_SETTINGS: '/organizations/me/auto-checkout-deduction-settings', // GET/PATCH organization auto-checkout deduction policy
    CHECK_IN_SELFIE_SETTINGS: '/organizations/me/check-in-selfie-settings', // GET/PATCH organization check-in selfie settings
    CREATE_ADMIN: '/organizations/admins', // POST endpoint for creating admin (note: if API_BASE_URL includes /api, this may need to be /organizations/admins)
  },

  // ============================================
  // Settings Endpoints
  // ============================================
  SETTINGS: {
    BASE: '/settings',
    BY_CATEGORY: (category) => `/settings/${category}`,
    UPDATE: (idOrCategory) => `/settings/${idOrCategory}`,
    CREATE: '/settings',
    DELETE: (id) => `/settings/${id}`,
    GEMINI: '/settings/gemini',
    LOCATIONS: '/settings/locations',
    GENERAL: '/settings/general',
    DEDUCTIONS: '/settings/deductions',
  },

  // ============================================
  // Invoice Endpoints
  // ============================================
  INVOICES: {
    BASE: '/invoices',
    BY_ID: (id) => `/invoices/${id}`,
    CREATE: '/invoices',
    UPDATE: (id) => `/invoices/${id}`,
    DELETE: (id) => `/invoices/${id}`,
    STATS: '/invoices/stats',
  },

  // ============================================
  // Pending Approvals (badge counts)
  // ============================================
  APPROVALS: {
    PENDING_COUNTS: '/approvals/pending-counts',
  },

  // ============================================
  // Dashboard Endpoints
  // ============================================
  DASHBOARD: {
    STATS: '/dashboard/stats',
    RECENT_ACTIVITY: '/dashboard/recent-activity',
    SUMMARY: '/dashboard/summary',
    SUMMARY_CARD: (category) => `/dashboard/summary/${category}`,
  },

  // ============================================
  // Chat Endpoints
  // ============================================
  CHAT: {
    EMPLOYEES: '/chat/employees',
    MESSAGES: (employeeId) => `/chat/messages/${employeeId}`,
    SEND_MESSAGE: '/chat/messages',
    MARK_AS_READ: (employeeId) => `/chat/messages/${employeeId}/read`,
    // Group Chat Endpoints
    GROUPS: '/chat/groups',
    MESSAGES_GENERAL: (page = 1, limit = 50) => {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', limit);
      return `/chat/messages/general?${params.toString()}`;
    },
    MESSAGES_DEPARTMENT: (departmentId, page = 1, limit = 50) => {
      const params = new URLSearchParams();
      params.append('departmentId', departmentId);
      params.append('page', page);
      params.append('limit', limit);
      return `/chat/messages/department?${params.toString()}`;
    },
    DELETE_MESSAGE: (messageId) => `/chat/messages/${messageId}`,
    SEARCH_MESSAGES: (groupType, q, departmentId, page = 1, limit = 20) => {
      const params = new URLSearchParams();
      params.append('groupType', groupType);
      params.append('q', q);
      params.append('page', page);
      params.append('limit', limit);
      if (departmentId) params.append('departmentId', departmentId);
      return `/chat/messages/search?${params.toString()}`;
    },
    MESSAGES_AROUND: (messageId, before = 25, after = 25) => {
      const params = new URLSearchParams();
      params.append('before', before);
      params.append('after', after);
      return `/chat/messages/around/${messageId}?${params.toString()}`;
    },
    PINNED_MESSAGES: (groupType, departmentId) => {
      const params = new URLSearchParams();
      if (departmentId) params.append('departmentId', departmentId);
      const qs = params.toString();
      return `/chat/groups/${groupType}/pinned${qs ? `?${qs}` : ''}`;
    },
    TOGGLE_REACTION: (messageId) => `/chat/messages/${messageId}/reactions`,
    PIN_MESSAGE: (messageId) => `/chat/messages/${messageId}/pin`,
    UNPIN_MESSAGE: (messageId) => `/chat/messages/${messageId}/pin`,
  },

  // ============================================
  // Log Endpoints
  // ============================================
  LOGS: {
    BASE: '/logs',
    STATS: '/logs/stats',
    CLEAR: '/logs/clear',
    EXPORT: '/logs/export',
  },

  // ============================================
  // Notification Endpoints
  // ============================================
  NOTIFICATIONS: {
    BASE: '/notifications/my',
    LIST: (page = 1, limit = 50) => `/notifications/my?page=${page}&limit=${limit}`,
    MARK_READ: (id) => `/notifications/my/${id}/read`,
    READ_ALL: '/notifications/my/read-all',
    DELETE: (id) => `/notifications/my/${id}`,
    DELETE_ALL: '/notifications/my',
    UNREAD_COUNT: '/notifications/unread-count',
  },

  // ============================================
  // Updates Feed Endpoints
  // ============================================
  UPDATES: {
    CHECK: '/updates/check',
    BY_ID: (id) => `/updates/${id}`,
  },

  // ============================================
  // Gemini/AI Endpoints
  // ============================================
  GEMINI: {
    CHAT: '/gemini/chat',
    AI: '/ai', // For fetching API key
  },

  // ============================================
  // Support / AI Ask (Search in system)
  // ============================================
  SUPPORT: {
    AI_ASK: '/support/ai/ask',
    TICKETS: '/support/tickets',
  },

  // ============================================
  // Equipment Endpoints
  // ============================================
  EQUIPMENT: {
    BASE: '/equipment',
    BY_ID: (id) => `/equipment/${id}`,
    CREATE: '/equipment',
    UPDATE: (id) => `/equipment/${id}`,
    DELETE: (id) => `/equipment/${id}`,
    ASSIGN: (id) => `/equipment/${id}/assign`,
    UNASSIGN: (id) => `/equipment/${id}/unassign`,
    REASSIGN: (id) => `/equipment/${id}/reassign`,
  },

  // ============================================
  // Reports Endpoints
  // ============================================
  REPORTS: {
    GENERAL_INFORMATION: '/reports/general-information/json',
    SALARY_INFORMATION: '/reports/salary-summary/json',
    EMPLOYMENT_INFORMATION: '/reports/employment-information/json',
    LEAVE_BALANCE: '/reports/leave-balance/json',
    ANNUAL_LEAVES: (year) => `/reports/annual-leaves/json?year=${year}`,
    CONTACT_INFORMATION: '/reports/contact-information/json',
    BIRTHDAYS: (month) => `/reports/birthdays/json?month=${month}`,
    BIRTHDAYS_UPCOMING: (days) => `/employees/birthdays/upcoming?days=${days}`,
    PROBATION_PERIODS: '/reports/probation-periods/json',
    FORMER_EMPLOYEES: '/reports/former-employees/json',
    BANK_ACCOUNTS: '/reports/bank-accounts/json',
    FIXED_ASSETS: '/equipment',
    ATTENDANCE_DATA: (startDate, endDate) => `/reports/attendance-data/json?startDate=${startDate}&endDate=${endDate}`,
    DISCIPLINARY: '/reports/disciplinary/json',
    PERMISSION_REQUESTS: (status) => `/reports/permission-requests/json?status=${status}`,
  },

  // ============================================
  // Upload Endpoints
  // ============================================
  UPLOAD: {
    BASE: '/upload',
    LIST: '/upload/list', // GET - List all uploaded documents
    MY_DOCUMENTS: '/upload/my-documents', // GET list / POST upload my document
    DELETE: (id) => `/upload/${id}`, // DELETE - Delete document by publicId (supports nested R2 keys)
    DOCUMENT: '/upload/document', // POST - Upload document to employees
    DOCUMENT_STATS: (publicId) => `/upload/documents/${publicId}/stats`, // GET - Get document read statistics
  },

  // ============================================
  // Tasks Endpoints
  // ============================================
  TASKS: {
    BASE: '/tasks',
    BY_ID: (id) => `/tasks/${id}`,
    CREATE: '/tasks',
    UPDATE: (id) => `/tasks/${id}`,
    DELETE: (id) => `/tasks/${id}`,
    ADD_UPDATE: (id) => `/tasks/${id}/update`,
    MARK_DONE: (id) => `/tasks/${id}/done`,
    REQUEST_TIME_EXTENSION: (id) => `/tasks/${id}/request-time-extension`,
    TIME_EXTENSION_REQUESTS: (page = 1, limit = 25, status = '') => {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', limit);
      if (status) params.append('status', status);
      return `/tasks/time-extension-requests?${params.toString()}`;
    },
    RESPOND_TIME_EXTENSION: (requestId) => `/tasks/time-extension-requests/${requestId}/respond`,
    BY_EMPLOYEE: (employeeId, page = 1, limit = 25) => `/tasks/employee/${employeeId}?page=${page}&limit=${limit}`,
    BY_DEPARTMENT: (departmentId, page = 1, limit = 25) => `/tasks/department/${departmentId}?page=${page}&limit=${limit}`,
  },

  // ============================================
  // Positions Endpoints
  // ============================================
  POSITIONS: {
    BASE: '/positions',
    CREATE: '/positions',
    BY_ORG: (orgId) => `/positions/by-org/${orgId}`,
    BY_ID: (id) => `/positions/${id}`,
    UPDATE: (id) => `/positions/${id}`,
    DELETE: (id) => `/positions/${id}`,
  },

  // ============================================
  // Shifts Endpoints
  // ============================================
  SHIFTS: {
    BASE: '/shifts',
    CREATE: '/shifts',
    BY_ID: (id) => `/shifts/${id}`,
    UPDATE: (id) => `/shifts/${id}`,
    DELETE: (id) => `/shifts/${id}`,
  },

  // ============================================
  // Tools Endpoints
  // ============================================
  TOOLS: {
    BASE: '/tools',
    BY_ID: (id) => `/tools/${id}`,
    CREATE: '/tools',
    UPDATE: (id) => `/tools/${id}`,
    DELETE: (id) => `/tools/${id}`,
    ASSIGN: (id) => `/tools/${id}/assign`,
    REMOVE: (id) => `/tools/${id}/remove`,
  },

  // ============================================
  // Team Leaders Endpoints
  // ============================================
  TEAM_LEADERS: {
    BASE: '/team-leaders',
    ME: '/team-leaders/me',
    BY_ID: (userId) => `/team-leaders/${userId}`,
    UPDATE: (userId) => `/team-leaders/${userId}`,
    CHANGE_TO_EMPLOYEE: (userId) => `/team-leaders/${userId}/change-to-employee`,
  },

  // ============================================
  // Orders Endpoints
  // ============================================
  ORDERS: {
    BASE: (page = 1, limit = 25) => {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', limit);
      return `/orders?${params.toString()}`;
    },
    BY_ID: (id) => `/orders/${id}`,
  },
};

/**
 * Implicit current contract (unprefixed paths on `${API_BASE_URL}` = `/api`).
 * Breaking module contracts use `/v2/...` via withApiVersion / *_V2 groups.
 */
export const DEFAULT_API_VERSION = 1;

/**
 * Prefix a relative endpoint with `/vN` when N > 1.
 * v1 stays unprefixed so existing `/api/<resource>` clients keep working.
 * @param {string} path
 * @param {number|string} [version=1]
 * @returns {string}
 */
export function withApiVersion(path, version = DEFAULT_API_VERSION) {
  if (path == null) return path;
  const raw = String(path);
  const normalized = Number(String(version).replace(/^v/i, ''));
  if (!normalized || normalized <= 1) return raw;
  if (/^\/v\d+(?=\/|$)/i.test(raw)) {
    return raw.replace(/^\/v\d+(?=\/|$)/i, `/v${normalized}`);
  }
  const clean = raw.startsWith('/') ? raw : `/${raw}`;
  return `/v${normalized}${clean}`;
}

/**
 * Clone an ENDPOINTS group onto a newer API version without rewriting keys.
 * @param {Record<string, string|Function>} group
 * @param {number|string} version
 */
export function versionEndpointGroup(group, version = DEFAULT_API_VERSION) {
  const out = {};
  for (const [key, value] of Object.entries(group || {})) {
    if (typeof value === 'function') {
      out[key] = (...args) => withApiVersion(value(...args), version);
    } else if (typeof value === 'string') {
      out[key] = withApiVersion(value, version);
    } else {
      out[key] = value;
    }
  }
  return out;
}

/**
 * Get endpoint with token and tenant headers
 * Helper function to get endpoint with authentication headers
 * يتم الحصول على التوكن والـ tenant من localStorage بعد الـ login
 * @param {string|Function} endpoint - Endpoint path or function
 * @param {...any} params - Parameters for endpoint function
 * @returns {Object} - Object with endpoint path and headers
 */
export const getEndpoint = (endpoint, ...params) => {
  const token = getToken();
  const tenantId = getTenantId();
  
  // If endpoint is a function, call it with params
  const path = typeof endpoint === 'function' ? endpoint(...params) : endpoint;
  
  return {
    path,
    headers: {
      ...(token && { Authorization: `Bearer ${token}` }),
      ...(tenantId && { 'X-Tenant-ID': tenantId }),
    },
  };
};

/**
 * Get full URL for endpoint
 * @param {string|Function} endpoint - Endpoint path or function
 * @param {...any} params - Parameters for endpoint function
 * @returns {string} - Full endpoint path
 */
export const getEndpointPath = (endpoint, ...params) => {
  if (typeof endpoint === 'function') {
    return endpoint(...params);
  }
  return endpoint;
};

/**
 * Resolve an endpoint on a specific API version.
 * @param {string|Function} endpoint
 * @param {number|string} version
 * @param {...any} params
 */
export const getVersionedEndpointPath = (endpoint, version, ...params) =>
  withApiVersion(getEndpointPath(endpoint, ...params), version);

/**
 * Get authentication headers
 * @returns {Object} - Headers object with token and tenant
 */
export const getAuthHeaders = () => {
  const token = getToken();
  const tenantId = getTenantId();
  
  return {
    ...(token && { Authorization: `Bearer ${token}` }),
    ...(tenantId && { 'X-Tenant-ID': tenantId }),
    'Content-Type': 'application/json',
    'X-API-Version': String(DEFAULT_API_VERSION),
  };
};

export default ENDPOINTS;
